import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';

import Button from 'src/components/ui/Button';
import { useGroupCompaniesQuery } from 'src/hooks/__generated__/queries';
import type { Company } from 'src/types/__generated__/graphql';
import Loader from 'src/components/ui/Loader';
import { isBottomDiv } from 'src/utils/isBottom';
import useAlerts from 'src/hooks/useAlerts';
import AgencyCard from 'src/components/company/AgencyCard';
import { styledComponent } from 'src/utils/styled';

const StyledBox = styledComponent(Box)<{
    $allowFetchMore: boolean;
}>`
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    ${({ $allowFetchMore }) =>
        $allowFetchMore &&
        `
        height: 25rem;
        margin-left: -0.5rem;
        margin-right: -0.5rem;
        margin-top: 1.75rem;
        overflow: auto;
    `};
`;

interface Props {
    allowFetchMore: boolean;
    limit: number;
    onSeeAllClick?: () => void;
    company: Pick<Company, 'id' | 'recommendationsReceivedCount'>;
}

const RecommendedGroupCompanies: React.FC<Props> = ({ allowFetchMore, company, limit, onSeeAllClick }) => {
    const { t } = useTranslation(['common', 'profile']);
    const { addAlert } = useAlerts();
    const [allDataLoaded, setAllDataLoaded] = useState(false);

    const {
        data: allGroupCompaniesData,
        loading,
        fetchMore: fetchMoreAllGroupCompanies,
    } = useGroupCompaniesQuery({
        variables: {
            groupId: company.id,
            limit,
            offset: 0,
            orderBy: { recommendationAggregate: 'DESC', name: 'ASC' },
            onlyWithRecommendations: true,
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            setAllDataLoaded(Boolean((data.groupCompanies?.results.length ?? 0) >= (data.groupCompanies?.total ?? 0)));
        },
    });

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);

        if (bottom && !allDataLoaded && !loading) {
            try {
                await fetchMoreAllGroupCompanies({
                    variables: {
                        offset: allGroupCompaniesData?.groupCompanies?.results.length,
                    },
                });
            } catch (error: unknown) {
                addAlert({
                    type: 'error',
                    message: (error as Error).message,
                });
            }
        }
    };

    const groupCompanies = allGroupCompaniesData?.groupCompanies;
    const groupCompaniesTotal = groupCompanies?.total ?? 0;

    return (
        <StyledBox $allowFetchMore={allowFetchMore} onScroll={allowFetchMore ? handleScroll : undefined}>
            {Boolean(groupCompanies) && (
                <Typography
                    marginLeft="0.5rem"
                    marginTop={company.recommendationsReceivedCount > 0 ? '1rem' : '0'}
                    marginBottom="0.5rem"
                    variant="h5"
                    data-target-id="recommended-group-companies-text"
                >
                    {t(allowFetchMore ? 'profile:recommendedMembers' : 'profile:recommendedMembersToo', {
                        count: groupCompaniesTotal,
                        plurial: groupCompaniesTotal > 1 ? 's' : '',
                        plurialVerb: groupCompaniesTotal > 1 ? 'sont' : 'est',
                    })}
                </Typography>
            )}
            <Box marginX="0.7rem">
                {groupCompanies?.results.map((groupCompany) => (
                    <AgencyCard
                        key={groupCompany?.id}
                        extraContentDisplayed="ratingsAndCompanyMembers"
                        agency={groupCompany as Company}
                    />
                ))}
            </Box>
            {loading ? <Loader /> : null}
            {!allowFetchMore && groupCompaniesTotal > limit && (
                <Box
                    display="flex"
                    justifyContent="center"
                    mt="1rem"
                    data-target-id="recommended-group-companies-see-all"
                >
                    <Button variant="outlined" onClick={onSeeAllClick}>
                        {t('profile:seeAll')}
                    </Button>
                </Box>
            )}
        </StyledBox>
    );
};

export default RecommendedGroupCompanies;
